import { motion } from "framer-motion";
import { SectionHeading } from "../components/SectionHeading";

const TENETS = [
  {
    n: "01",
    title: "Pixel-pure aesthetic",
    body: "Every site we ship is hand-tuned. Type rhythm, motion timing, color contrast — all dialed in like a synth patch, not poured out of a template.",
  },
  {
    n: "02",
    title: "Speed is a feature",
    body: "Performance budgets are part of the brief, not an afterthought. We optimize for first paint and felt latency, not Lighthouse cosplay.",
  },
  {
    n: "03",
    title: "We don't ghost",
    body: "Most agencies vanish after launch. We hand you a maintainable codebase, document the strange decisions, and stay reachable.",
  },
  {
    n: "04",
    title: "Bespoke, not boilerplate",
    body: "No theme purchases, no AI-slop landing pages, no \"we'll spin up a starter kit.\" Every project is composed for you.",
  },
];

export function About() {
  return (
    <section id="about" className="relative py-24 md:py-32 px-4 md:px-8">
      <div className="mx-auto max-w-[1400px]">
        <SectionHeading
          kicker="// ABOUT"
          title={
            <>
              A small studio.{" "}
              <span className="shimmer-text">Doing fewer things, better.</span>
            </>
          }
        />

        <div className="grid lg:grid-cols-[440px_1fr] gap-10 lg:gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <div className="bevel-out bg-bruise p-3">
              <div className="bevel-in bg-void p-4 aspect-[4/3] relative overflow-hidden crt-flicker">
                <div className="flex justify-between items-center mb-3">
                  <span className="font-terminal text-mystic text-xs">
                    cauldron@dev:~$
                  </span>
                  <span className="font-terminal text-acid text-xs">● online</span>
                </div>
                <Typewriter />
                <div
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, rgba(0,0,0,0) 0px, rgba(0,0,0,0) 2px, rgba(155,61,245,0.04) 3px, rgba(0,0,0,0) 4px)",
                  }}
                />
              </div>
            </div>
            <div className="mx-auto w-32 h-3 bg-bruise mt-1" />
            <div className="mx-auto w-48 h-2 bg-bruise" />
          </motion.div>

          <div>
            <p className="font-pixel text-xl md:text-2xl text-aura/90 leading-relaxed max-w-2xl">
              Cauldron Dev is a small, deliberate web studio. We take on a handful of projects at
              a time, give them everything we've got, and ship work that earns its place in your
              portfolio — and ours.
            </p>

            <ul className="mt-12 grid sm:grid-cols-2 gap-4">
              {TENETS.map((t, i) => (
                <motion.li
                  key={t.n}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className="bevel-out bg-grimoire p-1"
                >
                  <div className="bg-night p-4 border-2 border-black h-full">
                    <div className="font-display text-acid text-base mb-2">{t.n}</div>
                    <h3 className="font-display text-bone text-sm mb-2 leading-tight">
                      {t.title}
                    </h3>
                    <p className="font-body text-sm text-aura/80 leading-relaxed">{t.body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function Typewriter() {
  const lines = [
    { prompt: "$", text: "whoami" },
    { text: "cauldron_dev — small independent web studio", color: "text-mystic" },
    { prompt: "$", text: "cat manifesto.txt" },
    {
      text: "we make the web feel a little less generic,",
      color: "text-bone",
    },
    {
      text: "while still being fast, accessible, and",
      color: "text-bone",
    },
    {
      text: "actually maintainable. yes — really.",
      color: "text-bone",
    },
    { prompt: "$", text: "ls clients/" },
    { text: "founders/  studios/  artists/  weirdos/", color: "text-acid" },
    { prompt: "$", text: "_", blink: true },
  ];
  return (
    <div className="font-terminal text-base md:text-lg leading-snug">
      {lines.map((l, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 + i * 0.18, duration: 0.2 }}
        >
          {l.prompt && <span className="text-witch mr-2">{l.prompt}</span>}
          <span className={l.color ?? "text-aura"}>
            {l.text}
            {l.blink && <span className="animate-pulse">▌</span>}
          </span>
        </motion.div>
      ))}
    </div>
  );
}
